import { useRef, useState, useEffect } from 'react';
import { statusToBg } from './formatValue';

function MediaCard({ item }) {
    return (
        <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 w-[260px] rounded-lg overflow-hidden border border-gray-100 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-colors group"
        >
            {item.image && (
                <div className="h-[140px] overflow-hidden bg-gray-100 dark:bg-white/5">
                    <img
                        src={item.image}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            )}
            <div className="p-3">
                {item.source && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                        <img
                            src={`https://www.google.com/s2/favicons?domain=${item.source}&sz=16`}
                            alt=""
                            className="w-3.5 h-3.5 rounded-sm"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.source}</span>
                    </div>
                )}
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">
                    {item.title}
                </div>
                {item.time && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">{item.time}</div>
                )}
            </div>
        </a>
    );
}

function ReportCard({ item }) {
    return (
        <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 w-[280px] rounded-2xl border border-[#ececed] dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-colors px-4 py-3 flex flex-col gap-1"
        >
            <div className="flex items-center justify-between">
                <span className="text-base font-bold text-gray-900 dark:text-gray-100">{item.ticker || item.source}</span>
                {item.badge && (
                    <span className={`text-xs font-bold px-2 py-[3px] rounded-md ${statusToBg(item.badge_status || 'neutral')}`}>
                        {item.badge}
                    </span>
                )}
            </div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 leading-5">
                {item.title}
            </div>
            {item.value && (
                <div className="flex items-center justify-between">
                    <span className="text-sm text-[#636369] dark:text-gray-400">{item.value_label || 'Giá mục tiêu'}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.value}</span>
                </div>
            )}
            <div className="flex items-center justify-between">
                <span className="text-xs text-[#636369] dark:text-gray-500">{item.source}{item.time ? ` · ${item.time}` : ''}</span>
                {item.url && (
                    <span className="text-xs font-medium text-gray-900 dark:text-gray-100">Xem báo cáo</span>
                )}
            </div>
        </a>
    );
}

function DefaultCard({ item }) {
    return (
        <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 w-[240px] rounded-lg border border-gray-100 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-colors overflow-hidden"
        >
            {item.image && (
                <div className="h-[120px] overflow-hidden bg-gray-100 dark:bg-white/5">
                    <img src={item.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
            )}
            <div className="p-3">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 leading-snug">
                    {item.title}
                </div>
                {item.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{item.description}</div>
                )}
                {(item.source || item.time) && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                        {item.source}{item.time ? ` · ${item.time}` : ''}
                    </div>
                )}
            </div>
        </a>
    );
}

const CARD_COMPONENTS = {
    media: MediaCard,
    report: ReportCard,
    default: DefaultCard,
};

export default function CardList({ items, variant = 'default', title }) {
    const scrollRef = useRef(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowLeft(el.scrollLeft > 10);
        setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    };

    useEffect(() => {
        checkScroll();
        const el = scrollRef.current;
        if (el) el.addEventListener('scroll', checkScroll);
        return () => el?.removeEventListener('scroll', checkScroll);
    }, [items]);

    const scroll = (dir) => {
        const el = scrollRef.current;
        if (el) el.scrollBy({ left: dir * 300, behavior: 'smooth' });
    };

    if (!items || items.length === 0) return null;

    const CardComponent = CARD_COMPONENTS[variant] || CARD_COMPONENTS.default;

    return (
        <div>
            {title && <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</div>}
            <div className="relative group">
                {showLeft && (
                    <button
                        onClick={() => scroll(-1)}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}
                <div
                    ref={scrollRef}
                    className="flex gap-3 overflow-x-auto scrollbar-hide"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {items.map((item, i) => (
                        <CardComponent key={i} item={item} />
                    ))}
                </div>
                {showRight && (
                    <button
                        onClick={() => scroll(1)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}
